import React, { useEffect, useState } from 'react';
import { triggerNativeProfileChange } from '@/utils/nativeBridge';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import { setSelectedMember, clearSelectedMember } from '@/store/authSlice';
import { useManagedMembersContext } from '@/context/ManagedMembersContext';
import { Member } from '@/types/member';
import { IconCaretDownFilled } from '@tabler/icons-react';
import styles from './MProfilePicker.module.css';
import MSideDrawer from '@/components/ui/atoms/MSideDrawer';
import { useTranslation } from 'react-i18next';
import { updateNavigationSourceMemberId } from '@/utils/navigationSource';

export interface MProfilePickerProps {
  /**
   * Optional className for additional styling
   */
  className?: string;
  disabled?: boolean;
  onTriggerClick?: () => void;
  borderless?: boolean;
}

/**
 * MProfilePicker component for selecting between user profiles
 * 
 * Displays the currently selected profile and allows switching between profiles
 * Has two visual states: default and selected
 */
export const MProfilePicker: React.FC<MProfilePickerProps> = ({
  className = '',
  disabled = false,
  onTriggerClick,
  borderless = false,
}) => {
  const dispatch = useDispatch();
  const { managedMembers, loading, fetchManagedMembers } = useManagedMembersContext();
  const loggedInMember = useSelector((state: RootState) => state.auth.member);
  const selectedMember = useSelector((state: RootState) => state.auth.selectedMember);
  const [sheetOpen, setSheetOpen] = useState(false);

  const { t } = useTranslation();

  React.useEffect(() => {
    if (!managedMembers || managedMembers.length === 0) {
      fetchManagedMembers();
    }
  }, [managedMembers, fetchManagedMembers]);

  // For 'You' logic in the list
  const isYou = (member: Member) => member?.id === loggedInMember?.id;
  // When disabled, always show logged-in member as selected
  const selected = disabled
    ? loggedInMember
    : managedMembers.find((m) => m.id === (selectedMember?.id || loggedInMember?.id));

  useEffect(() => {
    // Whenever the selected member changes, notify native bridge
    const memberId = selectedMember?.id || loggedInMember?.id;
    if (memberId) {
      triggerNativeProfileChange({ memberId });
    }
    // Only run when selectedMember or loggedInMember changes
  }, [selectedMember?.id, loggedInMember?.id]);

  const handleSelectMember = (member: Member) => {
    if (member.id === loggedInMember?.id) {
      dispatch(clearSelectedMember());
      // Update navigation source to use logged-in member's ID
      updateNavigationSourceMemberId(loggedInMember.id);
    } else if ((selectedMember?.id || loggedInMember?.id) !== member.id) {
      dispatch(setSelectedMember(member));
      // Update navigation source to use newly selected member's ID
      updateNavigationSourceMemberId(member.id);
    }
    setSheetOpen(false);
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  if (!loading && managedMembers.length === 0) return null;

  return (
    <div data-profile-picker-row className={`${styles.profilePicker} ${className} ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}>
      <button
        data-profile-picker-trigger
        className={`
          ${styles.trigger}
          ${borderless ? styles.triggerBorderless : ''}
          ${selectedMember ? styles.triggerSelected : styles.triggerDefault}
          ${!loading && managedMembers.length === 1 ? 'cursor-not-allowed opacity-60' : ''}
          ${disabled ? 'cursor-not-allowed opacity-60' : ''}
        `}
        type="button"
        onClick={() => {
          if (disabled) return;
          if (onTriggerClick) {
            onTriggerClick();
            return;
          }
          if (!loading && managedMembers.length !== 1) {
            // Fetch managed members every time picker is opened to refresh expired S3 URLs
            fetchManagedMembers(true);
            setSheetOpen(!sheetOpen);
          }
        }}
        aria-haspopup="true"
        aria-expanded={sheetOpen ? "true" : "false"}
        disabled={disabled || (!loading && managedMembers.length === 1)}
      >
        <div className="relative flex flex-col items-start">
          <span className={styles.switchMemberLabel}>SWITCH MEMBER</span>

          <div className={styles.profilePickerTrigger}>
            <span
              className={`body-xl-bold ${styles.selectedMemberName} truncate ${loading ? 'invisible' : ''}`}
              style={{ display: 'inline-block' }}
            >
              {isYou(selected || loggedInMember)
                ? 'You'
                : ((selected || loggedInMember)?.nickname || (selected || loggedInMember)?.first_name || '')}
            </span>
            <IconCaretDownFilled size={16} stroke={2} />
          </div>

          {/* Skeleton overlay shown while loading. Positioned absolutely and won't change layout width. */}
          {loading && (
            <div className={`${styles.skeletonWrapper}`}>
              <div className={`${styles.avatar} ${styles.skeleton}`} />
              <div className={`h-4 w-16 ${styles.skeleton}`} />
            </div>
          )}
        </div>
      </button>

      <MSideDrawer
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title="Choose member"
      >
        {loading && (
          <div className="flex justify-center items-center py-8 text-gray-500">{t('common.loading')}</div>
        )}
        {!loading && [...managedMembers]
          .sort((a, b) => {
            const aId = a.id || a._id;
            const bId = b.id || b._id;
            const loggedInId = loggedInMember?.id || (loggedInMember as any)?._id;
            
            // Logged-in user always first
            if (aId === loggedInId) return -1;
            if (bId === loggedInId) return 1;
            
            // Sort others alphabetically by first name
            const aName = (a.first_name || '').toLowerCase();
            const bName = (b.first_name || '').toLowerCase();
            return aName.localeCompare(bName);
          })
          .map((member) => {
          const isSelected = (selectedMember?.id || loggedInMember?.id) === member.id;
          return (
            <button
              key={member.id}
              type="button"
              className={styles.memberCard}
              style={{
                border: isSelected ? '1.6px solid var(--colour-border-primary, #AD95D1)' : '1.6px solid transparent',
                background: 'var(--colour-background-brand-primary-50, #EDE8F5)'
              }}
              onClick={() => {
                if (disabled) return;
                handleSelectMember(member);
              }}
            >
              <div 
                className={styles.memberAvatar}
                style={{
                  border: isSelected ? '2px solid var(--neutral-N0, #FFF)' : 'none',
                  background: isSelected ? 'var(--colour-background-brand-primary-400, #7046AE)' : '#C8B7E0'
                }}
              >
                {member.avatar ? (
                  <img
                    src={member.avatar}
                    alt={member.first_name || ''}
                    className="h-10 w-10 rounded-full object-cover"
                    style={{ minWidth: 40 }}
                  />
                ) : (
                  <span style={{ color: isSelected ? '#FFF' : '#45168C' }}>
                    {getInitials(member.first_name, member.last_name)}
                  </span>
                )}
              </div>
              <span className={styles.memberName}>
                {isYou(member)
                  ? 'You'
                  : `${member.nickname || member.first_name}`}
              </span>
            </button>
          );
        })}
      </MSideDrawer>
    </div>
  );
};

export default MProfilePicker;
